import { Controller } from "@hotwired/stimulus"

// Click-to-create for the Zeitleiste's empty cells (see NewEntryCellComponent).
//
// One instance per grid, driven by data-action on each empty cell rather than a controller
// per cell — a month grid draws employees x days and would otherwise instantiate hundreds.
// For the same reason the template lists live here as a single optionsValue keyed by
// employee id: each cell names only its employee and its date. Every path is built
// server-side; this file only stamps the clicked cell's date onto it.
//
// An employee with a single option opens its drawer straight away; several show a picker
// first. On the Zeitleiste an option is a template (kind "timesheet") or an absence
// ("sick_note", "leave_request"); a row offering both shows the picker in two sections. An
// absence gets the clicked day as date_from and date_to instead of a timesheet's date.
//
// The Stundenzettel Matrix reuses this with one employee and a Baustelle per row: its cells
// also name a site, stamped onto the URL as site_id next to the date, and the ids of the
// templates allowed to book it — the employee's other templates are left out of that cell.
// A task row of the Matrix also names its task, stamped on as task_id.
//
// The picker is moved to <body> on connect: all three grids scroll inside an overflow-auto
// container, which clips anything anchored inside a cell.
export default class extends Controller {
  static values = {
    options: Object,
    menuTitle: String,
    timesheetSection: String,
    absenceSection: String,
  }

  connect() {
    this.menu = this.element.querySelector("[data-new-entry-menu]")
    this.itemTemplate = this.element.querySelector("[data-new-entry-item-template]")
    this.sectionTemplate = this.element.querySelector("[data-new-entry-section-template]")
    this.menuTitleElement = this.menu?.querySelector("[data-new-entry-menu-title]")
    this.menuItems = this.menu?.querySelector("[data-new-entry-menu-items]")

    if (this.menu) document.body.appendChild(this.menu)

    this.boundClickOutside = this.clickOutside.bind(this)
    this.boundKeydown = this.handleKeydown.bind(this)
    this.boundClose = this.close.bind(this)
    this.boundReclaimMenu = this.reclaimMenu.bind(this)

    document.addEventListener("click", this.boundClickOutside)
    document.addEventListener("keydown", this.boundKeydown)
    // The menu is position:fixed, so anything that moves the cell it points at leaves it
    // stranded. Scroll is captured because the grids scroll in an inner container.
    document.addEventListener("scroll", this.boundClose, true)
    window.addEventListener("resize", this.boundClose)
    document.addEventListener("turbo:before-cache", this.boundReclaimMenu)
  }

  disconnect() {
    document.removeEventListener("click", this.boundClickOutside)
    document.removeEventListener("keydown", this.boundKeydown)
    document.removeEventListener("scroll", this.boundClose, true)
    window.removeEventListener("resize", this.boundClose)
    document.removeEventListener("turbo:before-cache", this.boundReclaimMenu)
    // Moved out of the controller element, so Turbo's frame swap would leave it behind.
    this.menu?.remove()
  }

  // Turbo snapshots <body> before controllers disconnect, so a cached page would keep the
  // menu parked at body level and none inside this grid. Restoring that snapshot (browser
  // Back) would then leave connect() without a menu, and every multi-template cell would
  // silently open its first template instead of asking. Put it back before the snapshot.
  reclaimMenu() {
    this.close()
    if (this.menu) this.element.appendChild(this.menu)
  }

  open(event) {
    event.preventDefault()
    // Without this the same click bubbles to the document listener below and closes the
    // menu in the same tick it was opened.
    event.stopPropagation()

    const { employee, date, site, task, templates } = event.params
    const options = this.optionsFor(employee, date, site, templates, task)
    if (options.length === 0) return

    if (options.length === 1) {
      this.openDrawer(options[0].url)
      return
    }

    this.showMenu(event.currentTarget, options)
  }

  // The row's options, each with the clicked cell's date (and Baustelle, from the Matrix)
  // stamped onto the server-built URL. A cell naming template ids keeps only those. The
  // Matrix's options carry no kind: they are all templates.
  optionsFor(employeeId, date, siteId, templateIds, taskId) {
    const options = (this.optionsValue[String(employeeId)] || []).filter(
      (option) => !Array.isArray(templateIds) || templateIds.includes(option.id),
    )

    return options.map((option) => {
      const kind = option.kind || "timesheet"
      return {
        kind,
        name: option.name,
        url: this.urlWithCell(option.url, kind, date, siteId, taskId),
      }
    })
  }

  urlWithCell(url, kind, date, siteId, taskId) {
    const parsed = new URL(url, window.location.origin)
    if (kind === "timesheet") {
      parsed.searchParams.set("date", date)
    } else {
      parsed.searchParams.set("date_from", date)
      parsed.searchParams.set("date_to", date)
    }
    if (siteId) parsed.searchParams.set("site_id", siteId)
    if (taskId) parsed.searchParams.set("task_id", taskId)

    return `${parsed.pathname}${parsed.search}`
  }

  showMenu(cell, options) {
    if (!this.menu || !this.itemTemplate) {
      this.openDrawer(options[0].url)
      return
    }

    this.menuItems.replaceChildren()

    const timesheets = options.filter((option) => option.kind === "timesheet")
    const absences = options.filter((option) => option.kind !== "timesheet")

    if (absences.length === 0 || !this.sectionTemplate) {
      this.setMenuTitle(this.menuTitleValue)
      options.forEach((option) => this.appendItem(option))
    } else {
      this.setMenuTitle(null)
      this.appendSection(this.timesheetSectionValue, timesheets)
      this.appendSection(this.absenceSectionValue, absences)
    }

    this.menu.classList.remove("hidden")
    this.position(cell)
  }

  setMenuTitle(title) {
    if (!this.menuTitleElement) return

    this.menuTitleElement.textContent = title || ""
    this.menuTitleElement.classList.toggle("hidden", !title)
  }

  appendSection(title, options) {
    if (options.length === 0) return

    const heading = this.sectionTemplate.content.firstElementChild.cloneNode(true)
    heading.textContent = title
    this.menuItems.appendChild(heading)
    options.forEach((option) => this.appendItem(option))
  }

  appendItem(option) {
    const item = this.itemTemplate.content.firstElementChild.cloneNode(true)
    item.textContent = option.name
    item.addEventListener("click", (event) => {
      event.preventDefault()
      event.stopPropagation()
      this.close()
      this.openDrawer(option.url)
    })
    this.menuItems.appendChild(item)
  }

  // Anchored under the cell, then pulled back inside the viewport so a row at the right or
  // bottom edge of a month grid still shows the whole menu.
  position(cell) {
    const cellRect = cell.getBoundingClientRect()
    const menuRect = this.menu.getBoundingClientRect()
    const margin = 8

    let left = cellRect.left
    let top = cellRect.bottom + 4

    if (left + menuRect.width > window.innerWidth - margin) {
      left = Math.max(margin, window.innerWidth - menuRect.width - margin)
    }
    if (top + menuRect.height > window.innerHeight - margin) {
      top = Math.max(margin, cellRect.top - menuRect.height - 4)
    }

    this.menu.style.left = `${left}px`
    this.menu.style.top = `${top}px`
  }

  close() {
    this.menu?.classList.add("hidden")
  }

  clickOutside(event) {
    if (this.menu?.contains(event.target)) return

    this.close()
  }

  handleKeydown(event) {
    if (event.key === "Escape") this.close()
  }

  // Same drawer path the timeline uses for opening an entry: a turbo-stream fetch, because
  // the response appends an offcanvas rather than replacing the frame the cell lives in.
  // Every refusal this can hit (no template for that employee, Pundit) answers a stream
  // request with a flash stream, so a non-stream body here means the URL itself went stale.
  openDrawer(url) {
    const csrfToken = document.head
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content")

    fetch(url, {
      credentials: "same-origin",
      headers: {
        "X-CSRF-Token": csrfToken,
        Accept: "text/vnd.turbo-stream.html",
      },
    })
      .then(async (response) => {
        const contentType = response.headers.get("Content-Type") || ""
        const html = await response.text()

        if (contentType.includes("text/vnd.turbo-stream.html")) {
          Turbo.renderStreamMessage(html)
        } else {
          console.error("Unexpected response opening new entry form:", response.status)
        }
      })
      .catch((error) => {
        console.error("Error opening new entry form:", error)
      })
  }
}
